import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GrantPermissionToRolesResultSchema,
  PermissionDeclarationSchema,
  SensitivityCategorySchema,
  type GrantPermissionToRolesResult,
  type PermissionDeclarationDto,
  type RoleSummaryDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, postJson } from '../../shared/api/client';
import { useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';

const CATEGORIES = SensitivityCategorySchema.options;

/** What was granted, for the message shown once the window closes. */
export interface GrantedToRoles {
  readonly permission: string;
  readonly granted: readonly string[];
  readonly skipped: readonly string[];
}

/**
 * One permission granted to several roles in one change (SDD §11.4; FRD-USR-005). Business roles
 * can be ticked together; a privileged administration role is ticked only on purpose, with a
 * warning. Roles that already hold the permission are left as they are and reported. Every grant
 * is audited, exactly as on a role's own page.
 */
export function GrantToRolesDialog({
  open,
  roles,
  onClose,
}: {
  open: boolean;
  roles: readonly RoleSummaryDto[];
  onClose: (result?: GrantedToRoles) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<PermissionDeclarationDto | null>(null);
  const [category, setCategory] = useState('');
  const [ownRecordsOnly, setOwnRecordsOnly] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const catalogue = useQuery({
    queryKey: ['platform', 'permission-catalogue'],
    enabled: open,
    queryFn: () =>
      getData('/api/v1/platform/permission-catalogue', z.array(PermissionDeclarationSchema)),
  });
  const active = roles
    .filter((r) => r.status === 'active')
    .sort((a, b) => a.name.localeCompare(b.name));
  const business = active.filter((r) => !r.isPrivileged);
  const unmasked = permission?.action === 'view_unmasked';
  const canLimitToOwn = permission !== null && !permission.privileged && !unmasked;
  const name = (code: string) => roles.find((r) => r.code === code)?.name ?? code;
  const privilegedChosen = active.filter((r) => r.isPrivileged && chosen.has(r.code));
  const businessChosen = business.filter((r) => chosen.has(r.code));
  const reset = () => {
    setPermission(null);
    setCategory('');
    setOwnRecordsOnly(false);
    setChosen(new Set());
  };
  const toggle = (code: string, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(code);
    else next.delete(code);
    setChosen(next);
  };

  const grant = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/platform/permission-grants/to-roles',
        {
          recordType: permission?.recordType ?? '',
          action: permission?.action ?? '',
          roleCodes: [...chosen],
          ...(unmasked && category !== '' ? { sensitivityCategory: category } : {}),
          ...(canLimitToOwn && ownRecordsOnly ? { ownRecordsOnly: true } : {}),
        },
        GrantPermissionToRolesResultSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async (result: GrantPermissionToRolesResult) => {
      const summary: GrantedToRoles = {
        permission: permission?.description ?? '',
        granted: result.granted.map((g) => name(g.roleCode)),
        skipped: result.skipped.map(name),
      };
      reset();
      onClose(summary);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
    },
  });

  const count = chosen.size;
  return (
    <FormDialog
      open={open}
      title="Grant a permission to several roles"
      submitLabel={
        count === 0 ? 'Grant' : `Grant to ${String(count)} role${count === 1 ? '' : 's'}`
      }
      canSubmit={permission !== null && count > 0 && (!unmasked || category !== '')}
      submitting={grant.isPending}
      error={
        grant.isError ? grant.error.message : catalogue.isError ? catalogue.error.message : null
      }
      onClose={() => {
        grant.reset();
        reset();
        onClose();
      }}
      onSubmit={() => {
        grant.mutate();
      }}
    >
      <Autocomplete
        options={catalogue.data ?? []}
        value={permission}
        onChange={(_, value) => {
          setPermission(value);
          setCategory('');
        }}
        groupBy={(option) =>
          option.owningModule.charAt(0).toUpperCase() + option.owningModule.slice(1)
        }
        getOptionLabel={(option) => option.description}
        isOptionEqualToValue={(a, b) => a.recordType === b.recordType && a.action === b.action}
        loading={catalogue.isPending}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Permission"
            required
            helperText="Search by what the permission allows, for example “Announcement: view”."
          />
        )}
      />
      {unmasked && (
        <TextField
          select
          label="Sensitivity category"
          required
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
          }}
          helperText="Unmasked access is granted one sensitivity category at a time."
        >
          {CATEGORIES.map((value) => (
            <MenuItem key={value} value={value}>
              {value.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>
      )}
      {canLimitToOwn && (
        <FormControlLabel
          control={
            <Checkbox
              checked={ownRecordsOnly}
              onChange={(e) => {
                setOwnRecordsOnly(e.target.checked);
              }}
            />
          }
          label="Own records only — reaches only the holder's own employee record (self-service)"
        />
      )}
      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 0.5 }}
        >
          <Typography variant="subtitle2">Roles</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => {
                setChosen(
                  new Set([...business.map((r) => r.code), ...privilegedChosen.map((r) => r.code)]),
                );
              }}
            >
              Select all business roles
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={count === 0}
              onClick={() => {
                setChosen(new Set());
              }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
        <FormGroup role="group" aria-label="Roles to grant to">
          {active.map((role) => (
            <FormControlLabel
              key={role.code}
              control={
                <Checkbox
                  size="small"
                  checked={chosen.has(role.code)}
                  onChange={(e) => {
                    toggle(role.code, e.target.checked);
                  }}
                />
              }
              label={
                <span>
                  {role.name}
                  {role.isPrivileged && (
                    <Typography component="span" variant="caption" color="warning.main">
                      {' '}
                      · privileged administration
                    </Typography>
                  )}
                </span>
              }
            />
          ))}
        </FormGroup>
        <Typography variant="caption" color="text.secondary">
          Roles that already hold the permission are left as they are. Every holder of a ticked role
          gets the permission, and each grant is recorded in the audit trail.
        </Typography>
      </Box>
      {privilegedChosen.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {privilegedChosen.map((r) => r.name).join(', ')}{' '}
          {privilegedChosen.length === 1 ? 'is a' : 'are'} privileged administration role
          {privilegedChosen.length === 1 ? '' : 's'}: its holders administer the system. Grant it
          only if they need this permission.
        </Alert>
      )}
      {permission?.privileged === true && businessChosen.length > 0 && (
        <Alert severity="warning" variant="outlined">
          This is an administration permission. It only takes effect through a privileged role, so
          it does nothing for {businessChosen.map((r) => r.name).join(', ')}.
        </Alert>
      )}
    </FormDialog>
  );
}
